import React, { useState, useEffect } from 'react';
import { Camera, Upload, X, ChevronLeft } from 'lucide-react';
import imageCompression from 'browser-image-compression';
import * as Sentry from '@sentry/react';
import { getMerchantHeaderClasses, getMerchantButtonClasses } from '../utils/merchantColors';
import { t, pickConfigText, resolveConfigText } from '../utils/i18n';

const HEIC_EXT = /\.(heic|heif)$/i;
const HEIC_MIME = /^image\/(heic|heif)$/i;

// When client-side compression fails (a known browser-image-compression bug on
// some mobile WebKit builds), fall back to uploading the already-decoded file
// as-is — but only if it's small enough to upload without the compression step.
// Larger files are dropped and the user is allowed to continue without them.
const MAX_UNCOMPRESSED_BYTES = 6 * 1024 * 1024; // 6 MB

const isHeicFile = (file) =>
  !!file && (HEIC_EXT.test(file.name || '') || HEIC_MIME.test(file.type || ''));

/**
 * Normalize a thrown value into a real Error before reporting to Sentry.
 *
 * Browser image APIs used during processing (FileReader, <img>, and the
 * canvas pipeline inside browser-image-compression) signal failure by firing
 * an `error` event, so the promise rejects with a raw DOM Event
 * (e.g. ProgressEvent type=error) that has no message or stack. Sentry records
 * that as an opaque "Event `ProgressEvent` (type=error) captured as exception",
 * which is non-actionable and groups every unrelated failure together. Wrapping
 * non-Error rejections in a descriptive Error gives a meaningful title, a
 * stack, and per-step grouping.
 */
const toReportableError = (value, step) => {
  if (value instanceof Error) return value;

  let detail = '';
  if (typeof Event !== 'undefined' && value instanceof Event) {
    detail = value.type ? `${value.type} event` : 'DOM event';
    // FileReader / Image expose the underlying DOMException on target.error
    const targetError = value.target?.error;
    if (targetError) {
      detail += `: ${targetError.name || targetError.message || targetError}`;
    }
  } else if (typeof value === 'string') {
    detail = value;
  } else if (value != null) {
    try {
      detail = JSON.stringify(value);
    } catch {
      detail = String(value);
    }
  }

  const error = new Error(`Photo ${step} failed${detail ? ` (${detail})` : ''}`);
  error.name = 'PhotoProcessingError';
  return error;
};

/**
 * Convert a HEIC/HEIF file to a JPEG File. Uses heic2any (libheif WASM),
 * which is dynamically imported so the ~1.5 MB payload only ships when
 * a HEIC is actually picked.
 */
const convertHeicToJpeg = async (file) => {
  const { default: heic2any } = await import('heic2any');
  const result = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
  const blob = Array.isArray(result) ? result[0] : result;
  const baseName = (file.name || 'photo').replace(HEIC_EXT, '');
  return new File([blob], `${baseName || 'photo'}.jpg`, {
    type: 'image/jpeg',
    lastModified: Date.now(),
  });
};

/**
 * Dynamic Photo Upload Component
 *
 * Handles photo upload based on section configuration
 * Supports both booking-level and item-level photos
 */
const DynamicPhotoUpload = ({
  config,
  language = 'de',
  merchant = null,
  itemData = null, // For item-level photos: { bookingItem, inventoryItem }
  initialPhotos = [], // Array of File objects to initialize with
  onPhotosChange,
  onComplete,
  onBack = null // For item-level: callback to navigate to previous item
}) => {
  console.log('[DynamicPhotoUpload] Rendering - minPhotos:', config?.min_photos, 'itemData:', itemData?.bookingItem?.item_name, 'initialPhotos:', initialPhotos?.length);

  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  // Set once a photo fails processing in a way we can't recover from. Lets the
  // user proceed even if they're below min_photos — completing the return with
  // no photo is strictly better than blocking them on a device-specific bug.
  const [allowSkipAfterError, setAllowSkipAfterError] = useState(false);

  const minPhotos = config?.min_photos || 0;
  const maxPhotos = config?.max_photos || 10;

  // Initialize photos from initialPhotos prop (when navigating back)
  useEffect(() => {
    if (initialPhotos && initialPhotos.length > 0) {
      console.log('[DynamicPhotoUpload] Initializing with', initialPhotos.length, 'photos from initialPhotos prop');

      // Convert File objects to internal format with preview URLs
      const photosWithPreviews = initialPhotos.map((file, index) => ({
        file,
        preview: URL.createObjectURL(file),
        timestamp: Date.now() + index // Add index to make timestamps unique
      }));

      setPhotos(photosWithPreviews);
    }

    // Cleanup function to revoke object URLs when component unmounts
    return () => {
      photos.forEach(photo => {
        if (photo.preview) {
          URL.revokeObjectURL(photo.preview);
        }
      });
    };
  }, []); // Only run on mount

  console.log('[DynamicPhotoUpload] Computed minPhotos:', minPhotos, 'maxPhotos:', maxPhotos, 'currentPhotos:', photos.length);
  // Header: exact language or the built-in generic header (right language
  // beats merchant text in the wrong one). Instructions have no built-in
  // equivalent, so they fall back across languages instead of vanishing.
  const headerText = pickConfigText(config?.header_text, language) || t(language, 'photosHeader');
  const instructions = resolveConfigText(config?.instructions, language);
  const showInstructions = config?.show_instructions !== false;

  // Replace placeholders in text if item-level
  const getDisplayText = (text) => {
    if (!text || !itemData) return text;

    let result = text;
    if (itemData.bookingItem) {
      result = result.replace(/{item_name}/g, itemData.bookingItem.item_name || '');
      result = result.replace(/{size}/g, itemData.bookingItem.size || '');
      result = result.replace(/{code}/g, itemData.bookingItem.code || '');
    }
    if (itemData.inventoryItem) {
      result = result.replace(/{item_number}/g, itemData.inventoryItem.item_number || '');
    }
    return result;
  };

  const handlePhotoCapture = async (event) => {
    console.log('[DynamicPhotoUpload] ▶ Photo capture triggered, files:', event.target.files?.length);

    const files = Array.from(event.target.files || []);
    if (files.length === 0) {
      console.log('[DynamicPhotoUpload] No files selected');
      return;
    }

    console.log('[DynamicPhotoUpload] Processing', files.length, 'files');
    setUploading(true);
    setError(null);

    const newPhotos = [];
    const failed = [];
    let reachedMax = false;
    let unrecoverableFailure = false;

    for (const originalFile of files) {
      if (photos.length + newPhotos.length >= maxPhotos) {
        reachedMax = true;
        break;
      }

      const heic = isHeicFile(originalFile);
      let step = 'processing';
      // The post-HEIC-conversion file we attempt to compress. Kept in scope so
      // the catch block can fall back to it (never the raw HEIC original, which
      // would be stored mislabeled as JPEG).
      let workFile = null;

      try {
        step = heic ? 'heic_conversion' : 'compression';
        workFile = heic ? await convertHeicToJpeg(originalFile) : originalFile;

        step = 'compression';
        const compressedFile = await imageCompression(workFile, {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 1920,
          // Run on the main thread. The web-worker path in
          // browser-image-compression mis-detects its environment on some
          // mobile WebKit builds (iOS 26 / Chrome iOS), where it throws
          // "false is not a constructor". The uncompressed fallback below
          // still covers any remaining failures.
          useWebWorker: false,
          fileType: 'image/jpeg',
        });

        newPhotos.push({
          file: compressedFile,
          preview: URL.createObjectURL(compressedFile),
          timestamp: Date.now(),
        });
      } catch (err) {
        // Compression (or HEIC conversion) failed. If we still have a decoded
        // JPEG that's small enough to upload as-is, use it rather than dropping
        // the photo. Only valid when compression itself failed (workFile exists
        // and is a real JPEG) — if HEIC conversion failed we have no usable file.
        const usedUncompressedFallback =
          step === 'compression' && !!workFile && workFile.size <= MAX_UNCOMPRESSED_BYTES;

        if (usedUncompressedFallback) {
          newPhotos.push({
            file: workFile,
            preview: URL.createObjectURL(workFile),
            timestamp: Date.now(),
          });
        } else {
          failed.push(originalFile?.name || 'photo');
          unrecoverableFailure = true;
        }

        console.error('[DynamicPhotoUpload] Error processing photo:', originalFile?.name, 'step:', step, 'fallback:', usedUncompressedFallback, err);
        Sentry.captureException(toReportableError(err, step), {
          tags: { phase: 'photo_processing', step },
          extra: {
            fileName: originalFile?.name,
            fileType: originalFile?.type,
            fileSize: originalFile?.size,
            isHeic: heic,
            // Whether we recovered by uploading the uncompressed original.
            usedUncompressedFallback,
            // Preserve what the original rejection actually was, since wrapping
            // it loses this detail.
            originalErrorType: Object.prototype.toString.call(err),
          },
        });
      }
    }

    if (newPhotos.length > 0) {
      const updatedPhotos = [...photos, ...newPhotos];
      setPhotos(updatedPhotos);
      onPhotosChange?.(updatedPhotos.map(p => p.file));
    }

    // A photo we couldn't process or recover unblocks the min-photos gate, so
    // the user is never stuck on a device-specific failure.
    if (unrecoverableFailure) {
      setAllowSkipAfterError(true);
    }

    if (failed.length > 0) {
      const n = failed.length;
      setError(t(language, 'photosFailedCanContinue', n));
    } else if (reachedMax) {
      setError(t(language, 'maxPhotosReached', maxPhotos));
    }

    setUploading(false);

    // Reset the input so re-selecting the same failed file re-triggers onChange
    if (event.target) event.target.value = '';
  };

  const handleRemovePhoto = (index) => {
    const updatedPhotos = photos.filter((_, i) => i !== index);
    setPhotos(updatedPhotos);
    onPhotosChange?.(updatedPhotos.map(p => p.file));
  };

  const handleNext = () => {
    try {
      console.log('[DynamicPhotoUpload] ▶ handleNext clicked, photos:', photos.length, 'minPhotos:', minPhotos, 'canProceed:', photos.length >= minPhotos);

      if (photos.length < minPhotos && !allowSkipAfterError) {
        console.log('[DynamicPhotoUpload] ❌ Not enough photos, showing error');
        setError(t(language, 'minPhotosRequired', minPhotos));
        return;
      }

      console.log('[DynamicPhotoUpload] ✅ Calling onComplete with', photos.length, 'photos');
      onComplete?.(photos.map(p => p.file));
    } catch (error) {
      console.error('[DynamicPhotoUpload] ❌ ERROR in handleNext:', error);
      throw error;
    }
  };

  const handleSkip = () => {
    try {
      console.log('[DynamicPhotoUpload] ▶ handleSkip clicked, minPhotos:', minPhotos, 'canSkip:', minPhotos === 0);

      if (minPhotos > 0) {
        console.log('[DynamicPhotoUpload] ❌ Cannot skip, photos are required');
        setError(t(language, 'photosRequired'));
        return;
      }

      console.log('[DynamicPhotoUpload] ✅ Calling onComplete with empty photos');
      onComplete?.([]);
    } catch (error) {
      console.error('[DynamicPhotoUpload] ❌ ERROR in handleSkip:', error);
      throw error;
    }
  };

  const canProceed = photos.length >= minPhotos || allowSkipAfterError;
  const canSkip = minPhotos === 0;

  console.log('[DynamicPhotoUpload] About to render, canProceed:', canProceed, 'canSkip:', canSkip, 'uploading:', uploading);

  try {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-2xl bg-white rounded-lg shadow-lg overflow-hidden">
          {/* Header */}
          <div className={`${getMerchantHeaderClasses(merchant)} p-6`}>
            <h1 className="text-2xl font-bold">{getDisplayText(headerText)}</h1>
            {showInstructions && instructions && (
              <p className="mt-2 opacity-90">{getDisplayText(instructions)}</p>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {console.log('[DynamicPhotoUpload] ✓ Rendering content section')}

          {/* Photo Count */}
          <div className="text-center mb-6">
            <p className="text-gray-600">
              {photos.length} / {maxPhotos} {t(language, 'photosLabel')}
              {minPhotos > 0 && (
                <span className="ml-2 text-sm">
                  ({t(language, 'minAbbrev')} {minPhotos})
                </span>
              )}
            </p>
          </div>

          {console.log('[DynamicPhotoUpload] ✓ Rendering upload buttons')}

          {/* Upload Buttons */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:merchant-border-light hover:merchant-bg-light transition-colors">
              <Camera className="w-12 h-12 text-gray-400 mb-2" />
              <span className="text-sm text-gray-600">
                {t(language, 'camera')}
            </span>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handlePhotoCapture}
              disabled={uploading || photos.length >= maxPhotos}
              className="hidden"
            />
          </label>

          <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:merchant-border-light hover:merchant-bg-light transition-colors">
            <Upload className="w-12 h-12 text-gray-400 mb-2" />
            <span className="text-sm text-gray-600">
              {t(language, 'gallery')}
            </span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoCapture}
              disabled={uploading || photos.length >= maxPhotos}
              className="hidden"
            />
          </label>
        </div>

        {/* Uploading Indicator */}
        {uploading && (
          <div className="text-center text-gray-600 mb-4">
            {t(language, 'processingPhotos')}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {error}
          </div>
        )}

        {/* Photo Grid */}
        {photos.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            {photos.map((photo, index) => (
              <div key={index} className="relative group">
                <img
                  src={photo.preview}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-32 object-cover rounded-lg shadow-md"
                />
                <button
                  onClick={() => handleRemovePhoto(index)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove photo"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {photos.length === 0 && !uploading && (
          <div className="text-center text-gray-500 py-12">
            <Camera className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p>{t(language, 'noPhotos')}</p>
          </div>
        )}
          </div>

          {/* Footer Actions */}
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <div className="flex gap-4">
              {onBack && (
                <button
                  onClick={onBack}
                  className="flex items-center px-6 py-3 rounded-lg font-semibold transition-colors bg-gray-100 text-gray-700 hover:bg-gray-200"
                >
                  <ChevronLeft className="w-5 h-5 mr-1" />
                  {t(language, 'back')}
                </button>
              )}
              {canSkip && (
                <button
                  onClick={handleSkip}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t(language, 'skip')}
                </button>
              )}
              <button
                onClick={handleNext}
                disabled={!canProceed || uploading}
                className={`flex-1 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  canProceed && !uploading
                    ? getMerchantButtonClasses(merchant)
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {t(language, 'next')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    console.error('[DynamicPhotoUpload] ❌ RENDER ERROR:', error);
    console.error('[DynamicPhotoUpload] Stack:', error.stack);
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <h2 className="text-red-700 font-bold mb-2">Render Error</h2>
          <p className="text-red-600">{error.message}</p>
          <pre className="text-xs mt-2 text-red-500 overflow-auto">{error.stack}</pre>
        </div>
      </div>
    );
  }
};

export default DynamicPhotoUpload;
